import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ProcedureFlowServices } from "../../../services/ProcedureFlowServices";
import { State } from "country-state-city";
import { HospitalServices } from "../../../services/HospitalServices";
import { handleApiErrorResponses } from "../../../utils/ApisErrorHandling";
import {
  sanitizeDecimalCostInput,
  hasUserFilledAnyCostMappingInput,
} from "../../../utils/sanitizeCostInput";
import { toast } from "react-toastify";
import deleteIcon from "../../../assets/deleteIcon.svg";
import downArrowNew from "../../../assets/downArrowNew.svg";

const DEFAULT_STRATIFICATION_NAMES = [
  "Routine Ward",
  "HDU",
  "ICU-without Ventilator",
  "ICU- with Ventilator",
];

const defaultStratRows = () =>
  DEFAULT_STRATIFICATION_NAMES.map((name) => ({ name, cost: "" }));

function buildDefaultStratNameBaselineForTiers(tiersList) {
  return tiersList.map((tier) =>
    (tier.rows || []).map((_, ri) => DEFAULT_STRATIFICATION_NAMES[ri] ?? "")
  );
}

/** Matches API / Manage Scheme: true only for explicit true (null → false). */
function isTruthyProcedureFlag(v) {
  if (v === true || v === "true") return true;
  return false;
}

function normalizeDropdownSearch(s) {
  return String(s ?? "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ");
}

/** Procedure code (id) + name — list, trigger, and search text */
function formatProcedureCostOptionLabel(p) {
  const code = String(p?.id ?? "").trim();
  const name = String(p?.name ?? "").trim();
  if (code && name) return `${code} — ${name}`;
  return code || name || "";
}

export default function ProcedureCostMapping() {
  const navigate = useNavigate();
  const stratRowNameBaselineRef = useRef(null);
  const [stratification, setStratification] = useState(false);
  const [implantRows, setImplantRows] = useState([]);
  const [implantSectionExpanded, setImplantSectionExpanded] = useState(false);
  const [procedures, setProcedures] = useState([]);
  const [procedureLoading, setProcedureLoading] = useState(false);
  const [procedureLoadMore, setProcedureLoadMore] = useState(false);
  const [procedurePage, setProcedurePage] = useState(1);
  const [procedureHasMore, setProcedureHasMore] = useState(true);
  const [schemes, setSchemes] = useState([]);
  const [schemeLoadMore, setSchemeLoadMore] = useState(false);
  const [schemePage, setSchemePage] = useState(1);
  const [schemeHasMore, setSchemeHasMore] = useState(true);
  const [selectedScheme, setSelectedScheme] = useState(null);
  const [selectedCostType, setSelectedCostType] = useState("");
  const [selectedProcedure, setSelectedProcedure] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [hospitalList, setHospitalList] = useState([]);
  const [selectedHospital, setSelectedHospital] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tiers, setTiers] = useState([
    { tierNo: 1, rows: defaultStratRows(), cost: "" },
    { tierNo: 2, rows: defaultStratRows(), cost: "" },
    { tierNo: 3, rows: defaultStratRows(), cost: "" },
  ]);

  const resetForm = () => {
    stratRowNameBaselineRef.current = null;
    setStratification(false);
    setImplantRows([]);
    setImplantSectionExpanded(false);
    setSelectedScheme(null);
    setSelectedCostType("");
    setSelectedProcedure(null);
    setSelectedState(null);
    setSelectedHospital(null);
    setTiers([
      { tierNo: 1, rows: defaultStratRows(), cost: "" },
      { tierNo: 2, rows: defaultStratRows(), cost: "" },
      { tierNo: 3, rows: defaultStratRows(), cost: "" },
    ]);
  };
  const indianStates = State.getStatesOfCountry("IN").map(
    (state) => state.name,
  );
  const removeTier = (index) => {
    setTiers((prev) => {
      if (prev.length <= 1) return prev;
      return prev
        .filter((_, i) => i !== index)
        .map((t, i) => ({ ...t, tierNo: i + 1 }));
    });
  };

  const removeImplantRow = (index) => {
    setImplantRows((prev) => {
      const next = prev.filter((_, i) => i !== index);
      if (next.length === 0) {
        setImplantSectionExpanded(false);
      }
      return next;
    });
  };

  const getHospitalList = async () => {
    try {
      const response = await HospitalServices.gethospitals("", 1, 1500);
      setHospitalList(response?.data?.list ?? []);
    } catch (error) {
      handleApiErrorResponses(error);
    }
  };
  const PAGE_SIZE = 200;

  const fetchProcedures = async (schemeId, page = 1, append = false) => {
    if (!schemeId) {
      setProcedures([]);
      return;
    }
    try {
      if (page === 1) setProcedureLoading(true);
      else setProcedureLoadMore(true);
      const response = await ProcedureFlowServices.getProcedures({
        page,
        limit: PAGE_SIZE,
        procedureSchemeData: "ACTIVE",
        schemeId,
      });
      const rawList = response?.data?.list ?? [];
      const list = rawList.map((item) => {
        const pm = item.proceduremaster ?? {};
        const pid = item.procedureId ?? pm.procedureId;
        return {
          id: pid,
          name: pm.procedure ?? item.procedureId ?? "",
          isStratification: isTruthyProcedureFlag(pm.isStratification),
          isImplant: isTruthyProcedureFlag(pm.isImplant),
        };
      });
      if (append) {
        setProcedures((prev) => {
          const existingIds = new Set(prev.map((p) => p.id));
          const newItems = list.filter((p) => !existingIds.has(p.id));
          return [...prev, ...newItems];
        });
      } else {
        setProcedures(list);
      }
      setProcedureHasMore(rawList.length >= PAGE_SIZE);
      setProcedurePage(page + 1);
    } catch (error) {
      console.error("Failed to fetch procedures", error);
      setProcedureHasMore(false);
    } finally {
      setProcedureLoading(false);
      setProcedureLoadMore(false);
    }
  };

  const fetchSchemes = async (page = 1, append = false) => {
    try {
      if (append) setSchemeLoadMore(true);
      const response = await ProcedureFlowServices.getDefaultSchemeTypes({
        page,
        limit: PAGE_SIZE,
      });
      const rawList = Array.isArray(response?.data?.data) ? response.data.data : [];
      if (append) {
        setSchemes((prev) => {
          const existingIds = new Set(prev.map((s) => s.id));
          const newItems = rawList.filter((s) => !existingIds.has(s.id));
          return [...prev, ...newItems];
        });
      } else {
        setSchemes(rawList);
      }
      setSchemeHasMore(rawList.length >= PAGE_SIZE);
      setSchemePage(page + 1);
    } catch (err) {
      setSchemes((prev) => (append ? prev : []));
      setSchemeHasMore(false);
    } finally {
      if (append) setSchemeLoadMore(false);
    }
  };

  useEffect(() => {
    fetchSchemes(1, false);
  }, []);

  useEffect(() => {
    getHospitalList();
  }, []);

  /** When scheme changes, reset procedure selection and re-fetch procedures for the new scheme. */
  useEffect(() => {
    setProcedures([]);
    setSelectedProcedure(null);
    setProcedurePage(1);
    setProcedureHasMore(true);
    if (selectedScheme?.id) {
      fetchProcedures(String(selectedScheme.id), 1, false);
    }
  }, [selectedScheme]);

  // Clear hidden-field values when cost type changes
  useEffect(() => {
    const costType = String(selectedCostType || "").toUpperCase();

    if (costType === "NATIONAL") {
      if (selectedState) setSelectedState(null);
      if (selectedHospital) setSelectedHospital(null);
    }

    if (costType === "STATE") {
      if (selectedHospital) setSelectedHospital(null);
    }

    if (costType === "HOSPITAL") {
      if (selectedState) setSelectedState(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCostType]);

  /**
   * No procedure: leave implant UI as-is (section stays visible from first paint).
   * With procedure: sync stratification/tiers; clear implant rows unless master isImplant (then reset to collapsed).
   */
  useEffect(() => {
    if (!selectedProcedure?.id) {
      stratRowNameBaselineRef.current = null;
      return;
    }
    setStratification(!!selectedProcedure.isStratification);
    setImplantSectionExpanded(false);
    setImplantRows([]);
    const freshTiers = [
      { tierNo: 1, rows: defaultStratRows(), cost: "" },
      { tierNo: 2, rows: defaultStratRows(), cost: "" },
      { tierNo: 3, rows: defaultStratRows(), cost: "" },
    ];
    setTiers(freshTiers);
    stratRowNameBaselineRef.current =
      buildDefaultStratNameBaselineForTiers(freshTiers);
  }, [selectedProcedure?.id]);

  const handleSave = async () => {
    const costType = String(selectedCostType || "").toUpperCase();

    if (!selectedScheme?.id) {
      alert("Please select a scheme.");
      return;
    }
    if (!selectedProcedure?.id) {
      alert("Please select a procedure.");
      return;
    }
    if (
      !hasUserFilledAnyCostMappingInput(
        stratification,
        tiers,
        implantRows,
        stratification ? stratRowNameBaselineRef.current : null
      )
    ) {
      toast.error(
        "Please fill at least one tier cost, stratification name or cost, or implant field before saving."
      );
      return;
    }

    setSaving(true);
    try {

      // Build tier costs (either from direct input, or computed from stratification rows)
      const tierCosts = tiers.map((tier) => {
        if (!stratification) return Number(tier.cost || 0);
        return (tier.rows || []).reduce(
          (sum, r) => sum + Number(r?.cost || 0),
          0,
        );
      });

      const tierObject = tierCosts.reduce((acc, cost, idx) => {
        acc[`tier${idx + 1}`] = { cost };
        return acc;
      }, {});

      // Implants: include only rows with a name or cost
      const implants = implantRows
        .map((r) => ({
          implantName: (r?.implantName ?? "").trim(),
          implantCost: Number(r?.implantCost || 0),
        }))
        .filter((r) => r.implantName || r.implantCost);

      // Stratifications: flatten all tier rows; include only filled rows
      const stratifications = tiers
        .flatMap((t) => t.rows || [])
        .map((r) => ({
          name: (r?.name ?? "").trim(),
          cost: Number(r?.cost || 0),
        }))
        .filter((r) => r.name || r.cost);

      // Store per-tier stratification breakdown in nrp field so it round-trips via the API
      const nrpPayload = stratification
        ? {
            hasStratification: true,
            tierStratifications: tiers.reduce((acc, tier, idx) => {
              const rows = (tier.rows || [])
                .map((r) => ({
                  name: (r?.name ?? "").trim(),
                  cost: Number(r?.cost || 0),
                }))
                .filter((r) => r.name || r.cost);
              if (rows.length) acc[`tier${idx + 1}`] = rows;
              return acc;
            }, {}),
          }
        : null;

      const base = {
        schemeId: selectedScheme?.id,
        procedureId: selectedProcedure?.id,
        procedureCostMapping: costType,
      };

      let payloadObj = null;

      if (costType === "NATIONAL") {
        payloadObj = {
          ...base,
          countryId: "IN",
          nationalId: "IN",
          tiers: tierObject,
          ...(implants.length ? { implants } : {}),
          ...(stratification && stratifications.length
            ? { stratifications }
            : {}),
          nrp: nrpPayload,
        };
      } else if (costType === "STATE") {
        if (!selectedState) {
          alert("Please select a state.");
          return;
        }
        payloadObj = {
          ...base,
          stateId: selectedState,
          ...tierObject,
          ...(implants.length ? { implants } : {}),
          ...(stratification && stratifications.length
            ? { stratifications }
            : {}),
          nrp: nrpPayload,
        };
      } else if (costType === "HOSPITAL") {
        const hospitalId =
          selectedHospital?.id ??
          selectedHospital?.hospitalId ??
          selectedHospital?.code ??
          null;
        if (!hospitalId) {
          alert("Please select a hospital.");
          return;
        }
        payloadObj = {
          ...base,
          hospitalId,
          ...tierObject,
          ...(implants.length ? { implants } : {}),
          ...(stratification && stratifications.length
            ? { stratifications }
            : {}),
          nrp: nrpPayload,
        };
      } else {
        // Fallback: keep existing behavior but send tiers at root
        payloadObj = {
          ...base,
          stateId: selectedState || null,
          hospitalId: selectedHospital?.id ?? null,
          ...tierObject,
          ...(implants.length ? { implants } : {}),
          ...(stratification && stratifications.length
            ? { stratifications }
            : {}),
          nrp: nrpPayload,
        };
      }

      await ProcedureFlowServices.mapProcedureSchemeCost([payloadObj]);
      toast.success("Procedure cost mapping saved successfully.");
      resetForm();
      navigate("/dashboard/manage-procedure-page");
    } catch (error) {
      handleApiErrorResponses(error);
      toast.error("Failed to save procedure cost mapping. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const canSaveMapping =
    Boolean(selectedScheme?.id) &&
    Boolean(selectedProcedure?.id) &&
    hasUserFilledAnyCostMappingInput(
      stratification,
      tiers,
      implantRows,
      stratification ? stratRowNameBaselineRef.current : null
    );

  return (
    <div className="min-h-screen w-full bg-[radial-gradient(81.98%_338.81%_at_13.41%_276.08%,_rgba(90,198,159,0.5)_0%,_rgba(222,222,222,0.5)_100%)] flex justify-center py-10 font-lufga">
      <div className="w-full max-w-5xl px-6">
        <h2 className="text-l font-semibold text-gray-900 mb-8">
          Procedure - Cost Mapping
        </h2>

        <div className="space-y-6">
          <h1 className="font-lufga font-normal text-[50px] leading-[100%] tracking-[-0.07em] text-black whitespace-nowrap">
            Procedure - Cost Mapping
          </h1>
          {/* Row 1 */}
          <div className="flex gap-6">
            <Dropdown
              placeholder="Scheme"
              options={schemes}
              value={selectedScheme?.name || ""}
              onSelect={(scheme) => {
                setSelectedScheme(scheme);
                setSelectedCostType(scheme?.procedureCostMapping ?? "");
              }}
              onClear={() => {
                setSelectedScheme(null);
                setSelectedCostType("");
                setSelectedProcedure(null);
                setSelectedState(null);
                setSelectedHospital(null);
              }}
              labelKey="name"
              fieldLabel="Scheme Name"
              onOpen={() => {
                if (schemes.length === 0) fetchSchemes(1, false);
              }}
              onScrollToBottom={() => {
                if (schemeHasMore && !schemeLoadMore) fetchSchemes(schemePage, true);
              }}
              hasMore={schemeHasMore}
              loadMoreLoading={schemeLoadMore}
              enableSearch={true}
            />

            <Dropdown
              placeholder="Cost Type"
              options={["NATIONAL", "STATE", "HOSPITAL"]}
              value={selectedCostType}
              disabled
              fieldLabel="Cost Type"
              className="max-w-xs"
            />
          </div>
          <Dropdown
            placeholder={
              !selectedScheme
                ? "Select scheme first"
                : procedureLoading
                  ? "Loading..."
                  : "Procedure"
            }
            options={procedures}
            value={
              selectedProcedure
                ? formatProcedureCostOptionLabel(selectedProcedure)
                : ""
            }
            labelKey="name"
            formatOptionLabel={formatProcedureCostOptionLabel}
            fieldLabel="Procedure Name"
            disabled={!selectedScheme}
            onSelect={(procedure) => setSelectedProcedure(procedure)}
            onScrollToBottom={() => {
              if (procedureHasMore && !procedureLoadMore && selectedScheme?.id)
                fetchProcedures(String(selectedScheme.id), procedurePage, true);
            }}
            hasMore={procedureHasMore}
            loadMoreLoading={procedureLoadMore}
            enableSearch={true}
          />

          {(() => {
            const costType = String(selectedCostType || "").toUpperCase();
            const isKnown = ["NATIONAL", "STATE", "HOSPITAL"].includes(costType);

            // Default fallback (unknown cost types): keep existing UI visible
            const showIndia = !isKnown || costType === "NATIONAL" || costType === "STATE";
            const showState = !isKnown || costType === "STATE";
            const showHospital = !isKnown || costType === "HOSPITAL";

            const fields = [
              showIndia && { key: "india", el: <Dropdown placeholder="India" options={["India"]} value="India" fieldLabel="Country" /> },
              showState && { key: "state", el: <Dropdown placeholder="State" options={indianStates} value={selectedState || ""} onSelect={(state) => setSelectedState(state)} enableSearch={true} fieldLabel="State" /> },
              showHospital && { key: "hospital", el: <Dropdown placeholder="Hospital Name" options={hospitalList} value={selectedHospital?.name ?? ""} onSelect={(hospital) => setSelectedHospital(hospital)} labelKey="name" enableSearch={true} fieldLabel="Hospital Name" /> },
            ].filter(Boolean);

            if (fields.length === 0) return null;

            return (
              <div className="flex gap-6 flex-wrap">
                {fields.map(({ key, el }) => (
                  <div key={key} className="flex-1 min-w-[160px]">
                    {el}
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Implant: visible on first load; after a procedure is chosen, only if master isImplant */}
          {(!selectedProcedure?.id || selectedProcedure.isImplant) && (
          <div className="relative bg-white rounded-[40px] px-8 py-6 shadow-sm border border-[#E8E8E8]">
            <span className="text-sm font-semibold text-gray-800 block mb-4">Implants</span>
            {!implantSectionExpanded ? (
              <button
                type="button"
                onClick={() => {
                  setImplantSectionExpanded(true);
                  setImplantRows((prev) =>
                    prev.length ? prev : [{ implantName: "", implantCost: "" }]
                  );
                }}
                className="w-full flex items-center justify-center gap-2 py-4 rounded-[54px] border-2 border-solid border-[#5AC69F] bg-white text-[#5AC69F] font-lufga font-medium hover:bg-[#F4FBF8] transition-colors"
              >
                <span className="text-xl">+</span>
                ADD MORE
              </button>
            ) : (
              <>
                {implantRows.map((row, index) => (
                  <div key={index} className="flex gap-6 mb-4 items-center">
                    <div className="flex-1">
                      <Input
                        label="Implant Name"
                        placeholder=""
                        variant="implant"
                        value={row.implantName}
                        onChange={(e) => {
                          const next = [...implantRows];
                          next[index] = {
                            ...next[index],
                            implantName: e.target.value,
                          };
                          setImplantRows(next);
                        }}
                      />
                    </div>
                    <div className="flex-1">
                      <Input
                        label="Implant Cost"
                        placeholder=""
                        variant="implant"
                        value={row.implantCost}
                        onChange={(e) => {
                          const next = [...implantRows];
                          next[index] = {
                            ...next[index],
                            implantCost: sanitizeDecimalCostInput(e.target.value),
                          };
                          setImplantRows(next);
                        }}
                      />
                    </div>
                    <img
                      src={deleteIcon}
                      alt="delete"
                      className="w-5 h-5 flex-shrink-0 cursor-pointer hover:opacity-70 transition"
                      onClick={() => removeImplantRow(index)}
                    />
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() =>
                    setImplantRows((prev) => [
                      ...prev,
                      { implantName: "", implantCost: "" },
                    ])
                  }
                  className="mt-2 flex items-center justify-center gap-2 py-2 rounded-full border-2 border-solid border-[#5AC69F] bg-white text-[#5AC69F] font-lufga text-sm font-medium hover:bg-[#F4FBF8] transition-colors"
                >
                  <span>+</span>
                  ADD MORE
                </button>
              </>
            )}
          </div>
          )}

          {/* Stratification ? — same control as before; values sync when a procedure is selected */}
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">Stratification ?</span>

            <div
              className="relative w-32 h-9 rounded-full border-2 border-[#5AC69F] bg-white cursor-pointer"
              onClick={() => setStratification(!stratification)}
            >
              <div
                className={`absolute top-0 left-0 h-full w-1/2 rounded-full bg-[#5AC69FCC] transition-transform duration-300 ${
                  stratification ? "translate-x-0" : "translate-x-full"
                }`}
              />

              <div className="relative z-10 flex h-full text-sm font-medium">
                <div
                  className={`flex-1 flex items-center justify-center ${
                    stratification ? "text-black" : "text-gray-600"
                  }`}
                >
                  Yes
                </div>
                <div
                  className={`flex-1 flex items-center justify-center ${
                    !stratification ? "text-black" : "text-gray-600"
                  }`}
                >
                  No
                </div>
              </div>
            </div>
          </div>

          {/* Dynamic Tiers */}
          {tiers.map((tier, index) => (
            <div key={tier.tierNo}>
              {stratification ? (
                <TierBlock
                  title={`Tier ${tier.tierNo} Costing`}
                  rows={tier.rows}
                  onChangeRow={(rowIndex, field, value) => {
                    const nextVal =
                      field === "cost" ? sanitizeDecimalCostInput(value) : value;
                    const updated = [...tiers];
                    const nextRows = [...(updated[index].rows || [])];
                    nextRows[rowIndex] = { ...nextRows[rowIndex], [field]: nextVal };
                    updated[index].rows = nextRows;
                    setTiers(updated);
                  }}
                  onRemoveTier={null}
                  deleteIconSrc={deleteIcon}
                />
              ) : (
                <div className="flex gap-4 items-center">
                  <div className="flex-1">
                    <Input
                      label={`Tier ${tier.tierNo} Costing`}
                      placeholder=""
                      value={tier.cost}
                      onChange={(e) => {
                        const updated = [...tiers];
                        updated[index].cost = sanitizeDecimalCostInput(e.target.value);
                        setTiers(updated);
                      }}
                    />
                  </div>
                  {/* Temporarily hidden — tier delete not needed for now
                  {tiers.length > 1 && (
                    <img
                      src={deleteIcon}
                      alt="delete"
                      className="w-5 h-5 flex-shrink-0 cursor-pointer hover:opacity-70 transition"
                      onClick={() => removeTier(index)}
                    />
                  )}
                  */}
                </div>
              )}
            </div>
          ))}

          {/* Save */}
          <div className="flex justify-center pt-6 font-lufga">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !canSaveMapping}
              className="w-[380px] h-[80px] bg-[#5AC69FCC] text-black
               rounded-full text-lg font-medium shadow
               hover:scale-105 transition border-0
               flex items-center justify-center gap-2 font-lufga
               disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              {saving ? (
                <>
                  <span className="inline-block w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ================= COMPONENTS ================= */

function TierBlock({ title, rows, onChangeRow, onRemoveTier, deleteIconSrc }) {
  return (
    <div className="relative bg-white rounded-3xl px-6 py-6 shadow-sm space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">{title}</span>
        {/* Temporarily hidden — tier delete not needed for now
        {onRemoveTier && (
          <img
            src={deleteIconSrc}
            alt="delete"
            className="w-5 h-5 cursor-pointer hover:opacity-70 transition"
            onClick={onRemoveTier}
          />
        )}
        */}
      </div>

      {(rows || []).map((row, index) => (
        <div key={index} className="flex gap-6 items-center">
          <div className="flex-1">
            <Input
              label="Stratification Name"
              placeholder=""
              variant="implant"
              value={row?.name ?? ""}
              onChange={(e) => onChangeRow?.(index, "name", e.target.value)}
            />
          </div>
          <div className="flex-1">
            <Input
              label="Stratification Cost"
              placeholder=""
              variant="implant"
              value={row?.cost ?? ""}
              onChange={(e) => onChangeRow?.(index, "cost", e.target.value)}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function Input({ placeholder, label, variant = "default", value, onChange }) {
  const base = "w-full text-sm font-lufga outline-none";
  const variants = {
    default: "bg-white border border-solid border-[#E8E8E899] rounded-full px-6 py-4",
    implant: "border border-solid border-[#E8E8E899] bg-white rounded-full px-6 py-4",
  };

  if (label) {
    return (
      <div className="rounded-full border border-solid border-[#E8E8E899] bg-white px-6 py-3 min-h-[56px] flex flex-col justify-center">
        <div className="text-xs text-gray-500 font-medium mb-1">{label}</div>
        <input
          type="text"
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className="w-full text-sm font-lufga outline-none border-none bg-transparent p-0 placeholder-gray-400"
        />
      </div>
    );
  }

  return (
    <input
      type="text"
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      className={`${base} ${variants[variant]}`}
    />
  );
}

// function Dropdown({ placeholder, options = [], className = "" }) {
//   const [open, setOpen] = useState(false);
//   const [value, setValue] = useState("");
//   const ref = useRef(null);

//   useEffect(() => {
//     const handler = (e) => {
//       if (ref.current && !ref.current.contains(e.target)) {
//         setOpen(false);
//       }
//     };
//     document.addEventListener("mousedown", handler);
//     return () => document.removeEventListener("mousedown", handler);
//   }, []);

//   return (
//     <div
//       ref={ref}
//       className={`relative w-full bg-white rounded-full px-6 py-4 shadow-sm cursor-pointer ${className}`}
//       onClick={() => setOpen(!open)}
//     >
//       <span className={`text-sm ${value ? "text-gray-700" : "text-gray-400"}`}>
//         {value || placeholder}
//       </span>

//       <span
//         className={`absolute right-6 top-1/2 -translate-y-1/2 text-gray-400 transition-transform ${
//           open ? "rotate-180" : ""
//         }`}
//       >
//         ▾
//       </span>

//       {open && (
//         <div className="absolute z-20 left-0 right-0 mt-3 bg-white rounded-2xl shadow-lg overflow-hidden">
//           {options.map((opt) => (
//             <div
//               key={opt}
//               onClick={() => {
//                 setValue(opt);
//                 setOpen(false);
//               }}
//               className="px-6 py-3 text-sm hover:bg-[#F4FBF8] transition"
//             >
//               {opt}
//             </div>
//           ))}
//         </div>
//       )}
//     </div>
//   );
// }

function Dropdown({
  placeholder,
  options = [],
  value,
  onSelect,
  onClear,
  labelKey,
  fieldLabel,
  disabled = false,
  className = "",
  onOpen,
  onScrollToBottom,
  hasMore = false,
  loadMoreLoading = false,
  enableSearch = false,
  formatOptionLabel,
}) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const ref = useRef(null);
  const menuRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (open && enableSearch && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
    if (!open) {
      setSearchQuery("");
      setHighlightedIndex(-1);
    }
  }, [open, enableSearch]);

  const searchNeedle = normalizeDropdownSearch(searchQuery);
  const filteredOptions = enableSearch
    ? options.filter((opt) => {
        if (!searchNeedle) return true;
        if (typeof formatOptionLabel === "function") {
          const line = formatOptionLabel(opt);
          if (normalizeDropdownSearch(line).includes(searchNeedle)) return true;
          const code = String(opt?.id ?? opt?.procedureId ?? "");
          const name = labelKey ? String(opt[labelKey] ?? "") : "";
          if (normalizeDropdownSearch(code).includes(searchNeedle)) return true;
          if (normalizeDropdownSearch(name).includes(searchNeedle)) return true;
          return false;
        }
        const label = labelKey ? opt[labelKey] : opt;
        return normalizeDropdownSearch(String(label)).includes(searchNeedle);
      })
    : options;

  const handleMenuScroll = () => {
    const el = menuRef.current;
    if (!el || !onScrollToBottom || !hasMore || loadMoreLoading) return;
    const { scrollTop, clientHeight, scrollHeight } = el;
    if (scrollTop + clientHeight >= scrollHeight - 80) {
      onScrollToBottom();
    }
  };

  return (
    <div
      ref={ref}
      className={`relative w-full bg-white rounded-full px-6 py-4 shadow-sm 
      ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"} 
      ${className}`}
      onClick={() => {
        if (disabled) return;
        onOpen && onOpen();
        setOpen(!open);
      }}
    >
      <span className={`text-sm ${value ? "text-gray-700" : "text-gray-400"}`}>
        {value || placeholder}
      </span>

      <div className="absolute right-6 top-1/2 -translate-y-1/2 flex items-center gap-[5px]">
        {value && fieldLabel && (
          <span className="text-[13px] text-gray-400 font-lufga pointer-events-none">
            {fieldLabel}
          </span>
        )}
        {!disabled && value && onClear && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClear();
              setOpen(false);
            }}
            className="flex items-center justify-center p-0.5 text-gray-500 hover:text-gray-700 transition-colors border-0 bg-transparent"
            aria-label="Clear"
          >
            &#x2715;
          </button>
        )}
        {!disabled && (
          <img
            src={downArrowNew}
            alt=""
            width={22}
            height={12}
            className={`shrink-0 opacity-100 transition-transform ${open ? "rotate-180" : ""}`}
            aria-hidden
          />
        )}
      </div>

      {open && (
        <div
          ref={menuRef}
          onScroll={handleMenuScroll}
          className="absolute z-20 left-0 right-0 mt-3 bg-white rounded-2xl shadow-lg overflow-hidden min-w-[200px] max-h-[320px] flex flex-col"
        >
          {enableSearch && (
            <div className="sticky top-0 bg-white border-b border-gray-200 p-2 z-10">
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setHighlightedIndex(-1);
                }}
                onKeyDown={(e) => {
                  if (filteredOptions.length === 0) return;
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setHighlightedIndex((prev) =>
                      prev < filteredOptions.length - 1 ? prev + 1 : 0
                    );
                  } else if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setHighlightedIndex((prev) =>
                      prev > 0 ? prev - 1 : filteredOptions.length - 1
                    );
                  } else if (
                    e.key === "Enter" &&
                    highlightedIndex >= 0 &&
                    highlightedIndex < filteredOptions.length
                  ) {
                    e.preventDefault();
                    const opt = filteredOptions[highlightedIndex];
                    typeof onSelect === "function" && onSelect(opt);
                    setOpen(false);
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                placeholder="Search..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#7AD3AE] focus:border-transparent"
              />
            </div>
          )}
          <div className="overflow-y-auto flex-1">
            {(() => {
              if (filteredOptions.length === 0) {
                return (
                  <div className="px-6 py-3 text-sm text-gray-500 text-center">
                    No results found
                  </div>
                );
              }

              return filteredOptions.map((opt, index) => {
                const label = labelKey ? opt[labelKey] : opt;
                const displayLabel =
                  typeof formatOptionLabel === "function"
                    ? formatOptionLabel(opt)
                    : label;
                const key = labelKey && opt.id != null ? opt.id : `${label}-${index}`;
                const isHighlighted = highlightedIndex === index;
                return (
                  <div
                    key={key}
                    ref={
                      isHighlighted
                        ? (el) => el?.scrollIntoView({ block: "nearest", behavior: "smooth" })
                        : undefined
                    }
                    onClick={() => {
                      typeof onSelect === "function" && onSelect(opt);
                      setOpen(false);
                    }}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    title={displayLabel}
                    className={`px-6 py-3 text-sm cursor-pointer text-left ${
                      typeof formatOptionLabel === "function"
                        ? "whitespace-normal break-words"
                        : "whitespace-nowrap"
                    } ${
                      isHighlighted
                        ? "bg-[#F0F9F4] ring-1 ring-inset ring-[#5AC69F]"
                        : "hover:bg-[#F4FBF8]"
                    }`}
                  >
                    {typeof formatOptionLabel === "function" ? (
                      opt?.id ? (
                        <>
                          <span className="font-semibold text-[#1B5E3D]">
                            {String(opt.id)}
                          </span>
                          <span className="text-gray-500"> — </span>
                          <span>{label}</span>
                        </>
                      ) : (
                        <span>{displayLabel}</span>
                      )
                    ) : (
                      label
                    )}
                  </div>
                );
              });
            })()}
            {loadMoreLoading && (
              <div className="px-6 py-3 text-sm text-gray-500 text-center">
                Loading...
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
